import httpClient from '../../httpClient';
import { ListingTier } from '../../../models/Listing';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { InvitedBroker } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/models/InvitedBroker';
import { AxiosRequestConfig } from 'axios';
import { User } from 'models/User';
import { BossModeTrack } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/BossModeStats/types';
import {
  DashboardMetric,
  PaymentInvoiceSkippedFormProps,
} from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/ListingMetrics/types';
import { DealChangeLog } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/DealChangeLog/types';
import { getFileParams, getUploadedFile } from 'uploadFileUtils';
import { TransferResponse } from 'models/DealRoom';
import { AddBossModeAccountForm } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/BossModeAccounts/components/AddBossMode/components/AddBossModeAccount/AddBossModeAccount.component';
import { FilePreview, UploadedFileResponse } from 'models/File';

function changeTier(listingId: string, listingTier: ListingTier) {
  return httpClient.post(`portfolio/admin/listings/${listingId}/change-tier`, {
    listingTier,
  });
}

function addABroker(values: InvitedBroker) {
  return httpClient.post('admin/invite-broker', values);
}

function getInvitedBrokers(params?: PaginationParams) {
  return httpClient.get<PaginatedData<InvitedBroker>>('admin/brokers', {
    params,
  });
}

async function uploadBrokerFirmLogo(
  file: File,
  fileName: string,
  config?: AxiosRequestConfig,
) {
  const fileParams = getFileParams(fileName, file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `admin/upload-broker-firm-logo-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

type CheckTransferData = {
  isBrokerExist: boolean;
  canBeListingOwner: boolean;
};

function checkTransferEmail(email: string, dealRoomId: string) {
  return httpClient.post<CheckTransferData>(
    'portfolio/admin/check-transfer-email',
    {
      email,
      dealRoomId,
    },
  );
}

function transferToColleague(
  listingId: string,
  email: string,
  dealRoomId: string,
) {
  return httpClient.post<TransferResponse>(
    `portfolio/admin/listings/${listingId}/transfer-listing`,
    {
      email,
      dealRoomId,
    },
  );
}

function transferToNewUser(
  listingId: string,
  user: Partial<User>,
  dealRoomId: string,
) {
  return httpClient.post<TransferResponse>(
    `portfolio/admin/listings/${listingId}/transfer-listing`,
    {
      ...user,
      dealRoomId,
    },
  );
}

function deleteBrokerFirmLogo(fileKey: string) {
  return httpClient.delete(`admin/delete-broker-firm-logo?fileKey=${fileKey}`);
}

function remindUserVerify(listingId: string, email: string) {
  return httpClient.post(
    `/portfolio/admin/listings/${listingId}/transfer-listing-reminder`,
    { email },
  );
}

function getDashboardMetrics(params: PaginationParams) {
  return httpClient.get<PaginatedData<DashboardMetric>>(
    '/admin/dashboard-metrics',
    {
      params,
    },
  );
}

function getAdminDealLog(params?: PaginationParams) {
  return httpClient.get<PaginatedData<DealChangeLog>>(
    '/admin/deal-room-fields-tracks',
    {
      params,
    },
  );
}

function getDashMetSearchvalues(searchValue: string) {
  return httpClient.get<{ brokers: string[]; listings: string[] }>(
    `/admin/dashboard-metrics/${searchValue}`,
  );
}

function getBossModeTracks(params?: PaginationParams) {
  return httpClient.get<PaginatedData<BossModeTrack>>(
    'admin/boss-mode-tracks',
    { params },
  );
}

function getUsers(params?: PaginationParams) {
  return httpClient.get<PaginatedData<User>>('admin/search-users', { params });
}

function getBrokers(params?: PaginationParams) {
  return httpClient.get<Array<{ name: string; userId: string }>>(
    'admin/brokers-with-deal',
    {
      params,
    },
  );
}

function getDashboardMetricsCsv() {
  return httpClient.get<FilePreview>('/admin/dashboard-metrics/export-csv');
}

function getDealsCsv() {
  return httpClient.get<FilePreview>('/admin/deals/export-csv');
}

function paymentInvoiceRespond(
  id: string,
  data: PaymentInvoiceSkippedFormProps,
) {
  return httpClient.post<DashboardMetric>(
    `/admin/boss-mode-tracks/${id}/payment-respond`,
    data,
  );
}

function getBossModeAccounts(params?: PaginationParams) {
  return httpClient.get<PaginatedData<User>>('/boss-mode-users', {
    params,
  });
}

function removeBossModeAccount(
  bossModeId: string,
  removeAccountFromPlatform: boolean,
) {
  return httpClient.post(`/boss-mode-users/${bossModeId}/strip`, {
    removeAccountFromPlatform,
  });
}

function addBossModeAccount(values: Partial<AddBossModeAccountForm>) {
  return httpClient.post('/boss-mode-users/invite', values);
}

function promoteBossModeAccount(userIds: string[]) {
  return httpClient.post('/boss-mode-users/promote', { userIds });
}

function searchUsersForBossModeAccount(params: PaginationParams) {
  return httpClient.get<PaginatedData<User>>('/boss-mode-users/search', {
    params,
  });
}

export default {
  changeTier,
  checkTransferEmail,
  transferToColleague,
  transferToNewUser,
  addABroker,
  getInvitedBrokers,
  uploadBrokerFirmLogo,
  deleteBrokerFirmLogo,
  remindUserVerify,
  getDashboardMetrics,
  getDashMetSearchvalues,
  getBossModeTracks,
  getAdminDealLog,
  getUsers,
  getBrokers,
  getDashboardMetricsCsv,
  getDealsCsv,
  paymentInvoiceRespond,
  getBossModeAccounts,
  removeBossModeAccount,
  addBossModeAccount,
  searchUsersForBossModeAccount,
  promoteBossModeAccount,
};
